import { MouseEvent as ReactMouseEvent } from "react";
import Button from "../Button/Button";
import {
  PaginatorCurrentPageReportOptions,
  PaginatorPrevPageLinkOptions,
} from "primereact/paginator";
import Dropdown from "../Dropdown/Dropdown";
import {
  PaginationControl,
  paginationButtonClasses,
  rememberPaginationFocus,
} from "./paginationFocus";

const getTotalPages = (options: PaginatorPrevPageLinkOptions) =>
  Math.ceil((options.props.totalRecords ?? 0) / (options.props.rows || 1));

const onPageButtonClick =
  (
    options: PaginatorPrevPageLinkOptions,
    control: PaginationControl,
    focusKey?: string,
  ) =>
  (e: ReactMouseEvent<HTMLButtonElement>) => {
    if (focusKey) {
      rememberPaginationFocus(focusKey, control);
    }
    options.onClick(e);
  };

export const FirstPageButton = ({
  options,
  showOnPages,
  focusKey,
}: {
  options: PaginatorPrevPageLinkOptions;
  showOnPages: number;
  focusKey?: string;
}) => {
  return getTotalPages(options) < showOnPages ? null : (
    <Button
      ariaLabel="first page"
      dataCy="first_page"
      variant="secondary"
      image="chevron-double-left"
      className={paginationButtonClasses.first}
      disabled={options.disabled}
      onClick={onPageButtonClick(options, "first", focusKey)}
    />
  );
};

export const PrevPageButton = ({
  options,
  focusKey,
}: {
  options: PaginatorPrevPageLinkOptions;
  focusKey?: string;
}) => (
  <Button
    ariaLabel="previous page"
    dataCy="previous_page"
    variant="secondary"
    image="chevron-left"
    className={paginationButtonClasses.prev}
    disabled={options.disabled}
    onClick={onPageButtonClick(options, "prev", focusKey)}
  />
);

export const NextPageButton = ({
  options,
  focusKey,
}: {
  options: PaginatorPrevPageLinkOptions;
  focusKey?: string;
}) => (
  <Button
    ariaLabel="next page"
    dataCy="next_page"
    variant="secondary"
    image="chevron-right"
    className={paginationButtonClasses.next}
    disabled={options.disabled}
    onClick={onPageButtonClick(options, "next", focusKey)}
  />
);

export const LastPageButton = ({
  options,
  showOnPages,
  focusKey,
}: {
  options: PaginatorPrevPageLinkOptions;
  showOnPages: number;
  focusKey?: string;
}) => {
  return getTotalPages(options) < showOnPages ? null : (
    <Button
      ariaLabel="last page"
      dataCy="last_page"
      variant="secondary"
      image="chevron-double-right"
      className={paginationButtonClasses.last}
      disabled={options.disabled}
      onClick={onPageButtonClick(options, "last", focusKey)}
    />
  );
};

export const ItemsPerPageDropdown = ({
  rowsPerPageText,
  pageSizeOptions,
  onPageChange,
  selectedPageSize,
}: {
  rowsPerPageText: string;
  pageSizeOptions: {
    id: number;
    value: number;
    name: string;
    isSelected: boolean;
  }[];
  onPageChange:
    | ((nextPage: { offset: number; limit: number }) => void)
    | undefined;
  selectedPageSize: {
    id: number;
    value: number;
    name: string;
    isSelected: boolean;
  };
}) => (
  <span className="rowsPerPageDropdown">
    <span data-cy="rowsPerPageText" className="mr-8">
      {rowsPerPageText}
    </span>
    <Dropdown
      dataCy="rowsPerPageDropdown"
      width={70}
      items={pageSizeOptions}
      onSelectItem={(item) => {
        onPageChange?.({
          offset: 0,
          limit: item.id as number,
        });
      }}
      selectedItem={selectedPageSize}
      placeholder=""
    />
  </span>
);

export const PageReport = ({
  options,
  tableTexts,
}: {
  options: PaginatorCurrentPageReportOptions;
  tableTexts: {
    showing: string;
    of: string;
    results: string;
  };
}) => (
  <span className="table-results" data-cy={"table_results"}>
    {tableTexts.showing} {options.first}-{options.last} {tableTexts.of}{" "}
    {options.totalRecords} {tableTexts.results}
  </span>
);
